"use client";

import { useLazyQuery } from "@apollo/client/react";
import { createUserAssociation, deleteUserAssociation } from "deso-protocol";
import Link from "next/link";
import type { ReactNode } from "react";
import { memo } from "react";
import { useCallback, useContext, useState } from "react";
import {
  LuBan,
  LuCopy,
  LuEllipsis as LuMoreHorizontal,
  LuFlag,
  LuLanguages,
  LuPencil,
  LuPin,
  LuPinOff,
  LuShare,
  LuShield,
} from "react-icons/lu";

import { waitForTxn } from "@/backend/api-helpers";
import { pinPost, unpinPost } from "@/backend/focus";
import { setDialog } from "@/components/app/dialogs/dialog-provider";
import { focusIOSKeyboardBridge } from "@/components/app/editor-v3/editor-dom-focus";
import { TriggerLoggedInModalWindow } from "@/components/shared/is-logged-in";
import {
  PostWeightLazyModal,
  UserWeightLazyModal,
} from "@/components/shared/LazyDialog";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import {
  DropdownDrawer,
  DropdownDrawerContent,
  DropdownDrawerItem,
  DropdownDrawerSeparator,
  DropdownDrawerSub,
  DropdownDrawerSubContent,
  DropdownDrawerSubTrigger,
  DropdownDrawerTrigger,
} from "@/components/ui/dropdown-drawer";
import { useToast } from "@/components/ui/use-toast";
import { useFeedContext } from "@/contexts/feed-context";
import { usePostContext } from "@/contexts/post-context";
import { UserContext } from "@/contexts/user-context";
import type { Account } from "@/graphql/codegen/graphql";
import { UserAssociationsDocument } from "@/graphql/codegen/graphql";
import { useBlockUser } from "@/hooks/use-block-user";
import {
  AssociationPinValue,
  AssociationType,
  DEFAULT_REPORT_TYPE,
  POST_REPORT_TYPE,
  USER_REPORT_TYPE,
} from "@/utils/constants";
import { DIALOG } from "@/utils/dialog-routes";
import { copyTextToClipboard, isAbortError } from "@/utils/helpers";
import type { PartialAccount } from "@/utils/types";
import { getUserDisplayName, getUserName } from "@/utils/usernames";

import { HidePostAction } from "./hide-post-action";

interface PostActionsProps {
  account?: PartialAccount | null;
  compact?: boolean;
  isComment?: boolean;
  children?: ReactNode;
  extraItems?: ReactNode;
}
export const PostActionsDropdown = memo(
  ({ compact = false, account, children, extraItems }: PostActionsProps) => {
    const {
      postHash,
      post: outerPost,
      repost,
      isPureRepost,
      updatePost,
      onUserBlock,
      isPinned,
      setPinnedPost,
      pinnedPostAssociationId,
      setPinnedPostAssociationId,
      isComment,
    } = usePostContext();

    const post = isPureRepost ? repost : outerPost;

    // Note: we need to use useFeedContext directly to have ability to pin/unpin post as an Admin from personal Feed | Search feed
    const { addAdminPinnedPost, removeAdminPinnedPost, isAdminPinnedPost } =
      useFeedContext();
    const { toast } = useToast();
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [postWeightOpen, setPostWeightOpen] = useState(false);
    const [userWeightOpen, setUserWeightOpen] = useState(false);
    const { blockUser, loading: blockingUser } = useBlockUser();
    const { isAdmin, isElevated, isSuperAdmin, currentUserPublicKey } =
      useContext(UserContext);
    const isPinnedByAdmin = isAdminPinnedPost(postHash);
    const posterPublicKey = post?.poster?.publicKey;
    const postLink = `${process.env.NEXT_PUBLIC_FOCUS_URI}/post/${postHash}`;

    const isUserBlacklisted = post?.poster?.isBlacklisted;
    const isUserGreylisted = (post?.poster?.isGreylisted?.totalCount ?? 0) > 0;
    const isUserNSFW = (post?.poster?.isNSFW?.totalCount ?? 0) > 0;

    const isPostNSFW = (post?.nsfwCount?.totalCount ?? 0) > 0;
    const hiddenFromForYou = (post?.hideFromForYouCount?.totalCount ?? 0) > 0;

    if (!posterPublicKey) {
      throw new Error("Poster public key is required");
    }

    const [getPinnedPostUserAssociation] = useLazyQuery(
      UserAssociationsDocument,
      {
        fetchPolicy: "network-only",
      },
    );
    const { confirm } = useConfirm();

    const openEditPostDialog = useCallback(() => {
      focusIOSKeyboardBridge();
      setOpen(false);

      // Let the mobile action drawer render its closed state before the routed
      // editor mounts, otherwise the exiting drawer can intercept the first
      // editor tap and trigger the dirty-close guard.
      requestAnimationFrame(() => {
        setDialog(DIALOG.EditPost, {
          open: true,
          replace: true,
          params: {
            postHash,
          },
          props: {
            onPostUpdated: (updatedPost) => {
              if (!updatedPost.postHash) {
                return;
              }

              updatePost(updatedPost.postHash, updatedPost);
            },
            post,
          },
        });
      });
    }, [post, postHash, updatePost]);

    const onBlockUser = async () => {
      const confirmed = await confirm({
        title: `Are you sure you want to block ${getUserName({ account })}?`,
        primaryCTA: "Block",
        type: "warning",
      });

      if (!post.poster?.publicKey || !confirmed) {
        return;
      }
      await blockUser(post.poster?.publicKey, () => {
        if (onUserBlock) {
          onUserBlock(post.poster?.publicKey as string);
        }
        setOpen(false);
      });
    };

    const onPinActionClick = useCallback(async () => {
      try {
        setLoading(true);

        if (pinnedPostAssociationId) {
          await deleteUserAssociation({
            TransactorPublicKeyBase58Check: currentUserPublicKey,
            AssociationID: pinnedPostAssociationId,
          });
        }

        if (isPinned) {
          setPinnedPostAssociationId?.(null);
          setPinnedPost?.(null);
          toast({
            title: "Success",
            description: "This post was unpinned from your profile",
            variant: "success",
          });
        } else {
          const data = await createUserAssociation({
            TransactorPublicKeyBase58Check: currentUserPublicKey,
            TargetUserPublicKeyBase58Check: currentUserPublicKey,
            AssociationType: AssociationType.pin,
            AssociationValue: AssociationPinValue.PIN,
            ExtraData: {
              postHash,
            },
          });

          if (!data.submittedTransactionResponse?.TxnHashHex) {
            throw new Error(
              "Transaction was not constructed. Please try again.",
            );
          }

          await waitForTxn({
            transactionHash: data.submittedTransactionResponse?.TxnHashHex,
          });

          // Refetching user Association to get the pinned post
          const userPinnedAssociationRequest = getPinnedPostUserAssociation({
            variables: {
              filter: {
                associationType: { equalTo: AssociationType.pin },
                target: {
                  publicKey: {
                    equalTo: currentUserPublicKey,
                  },
                },
              },
            },
          });
          userPinnedAssociationRequest.retain?.();
          const { data: userPinnedAssociationPostData } =
            await userPinnedAssociationRequest;

          const pinnedPostAssociationId =
            userPinnedAssociationPostData?.userAssociations?.nodes[0]
              ?.associationId ?? null;

          setPinnedPostAssociationId?.(pinnedPostAssociationId);
          setPinnedPost?.(post);
          toast({
            title: "Success",
            description: "This post was successfully pinned to your profile",
            variant: "success",
          });
        }
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        toast({
          title: "Error",
          description: "An error occurred while pinning post",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    }, [
      getPinnedPostUserAssociation,
      isPinned,
      pinnedPostAssociationId,
      post,
      postHash,
      setPinnedPost,
      setPinnedPostAssociationId,
      toast,
      currentUserPublicKey,
    ]);

    const onPinAdminPostClick = useCallback(async () => {
      if (!postHash || !currentUserPublicKey || !post) {
        return;
      }
      setLoading(true);
      try {
        if (isPinnedByAdmin) {
          await unpinPost(currentUserPublicKey, postHash);
          removeAdminPinnedPost(postHash);
          toast({ title: "Post unpinned successfully", variant: "success" });
        } else {
          await pinPost(currentUserPublicKey, postHash);
          addAdminPinnedPost(post);
          toast({ title: "Post pinned successfully", variant: "success" });
        }
      } catch {
        toast({ title: "Error pinning post", variant: "destructive" });
      } finally {
        setLoading(false);
      }
    }, [
      postHash,
      currentUserPublicKey,
      post,
      isPinnedByAdmin,
      removeAdminPinnedPost,
      toast,
      addAdminPinnedPost,
    ]);

    const onCopyLink = useCallback(async () => {
      try {
        await copyTextToClipboard(postLink);
        toast({
          variant: "success",
          title: "Success",
          duration: 2500,
          description: "Successfully copied to your clipboard.",
        });
      } catch {
        toast({
          title: "Error",
          description: "An error happened when copying the text.",
          variant: "destructive",
        });
      }
    }, [postLink, toast]);

    const openPostReport = useCallback(
      (defaultType = DEFAULT_REPORT_TYPE, showTypes = true) => {
        setDialog(DIALOG.ReportPost, {
          open: true,
          replace: true,
          params: {
            allowReverse: String(isAdmin),
            defaultType,
            hiddenFromForYou: String(hiddenFromForYou),
            isNSFW: String(isPostNSFW),
            postHash,
            showTypes: String(showTypes),
          },
        });
      },
      [hiddenFromForYou, isAdmin, isPostNSFW, postHash],
    );

    const openProfileReport = useCallback(
      (defaultType = DEFAULT_REPORT_TYPE, showTypes = true) => {
        setDialog(DIALOG.ReportUser, {
          open: true,
          replace: true,
          params: {
            allowReverse: String(isAdmin),
            defaultType,
            isBlacklisted: String(isUserBlacklisted ?? false),
            isGreylisted: String(isUserGreylisted),
            isNSFW: String(isUserNSFW),
            publicKey: posterPublicKey,
            showTypes: String(showTypes),
          },
        });
      },
      [
        isAdmin,
        isUserBlacklisted,
        isUserGreylisted,
        isUserNSFW,
        posterPublicKey,
      ],
    );

    const isCurrentUserAccount =
      currentUserPublicKey === post.poster?.publicKey;

    const PinIcon = isPinned ? LuPinOff : LuPin;
    const trigger = compact ? (
      <Button
        variant="outline"
        size="icon"
        className="size-auto border-none p-3"
      >
        <LuMoreHorizontal />
      </Button>
    ) : (
      <Button variant="outline" size="icon" className="rounded-full">
        <LuMoreHorizontal />
      </Button>
    );

    return (
      <div>
        {isSuperAdmin && (
          <>
            <PostWeightLazyModal
              open={postWeightOpen}
              postHash={postHash}
              rootProps={{ onOpenChange: setPostWeightOpen }}
            />
            <UserWeightLazyModal
              open={userWeightOpen}
              publicKey={posterPublicKey}
              rootProps={{ onOpenChange: setUserWeightOpen }}
            />
          </>
        )}
        <DropdownDrawer
          open={open}
          onOpenChange={setOpen}
          title="Post actions"
          historyName="post-actions"
        >
          <DropdownDrawerTrigger
            disabled={loading || blockingUser}
            render={trigger}
          />
          <DropdownDrawerContent align="end">
            {children}
            {isCurrentUserAccount && (
              <>
                <DropdownDrawerItem
                  closeOnClick={false}
                  onClick={openEditPostDialog}
                >
                  <LuPencil className="mr-2 size-4 text-muted" />
                  <span>Edit {isComment ? "Comment" : "Post"}</span>
                </DropdownDrawerItem>
                {!isComment && (
                  <DropdownDrawerItem onClick={onPinActionClick}>
                    <PinIcon className="mr-2 size-4 text-muted" />
                    <span>
                      {isPinned
                        ? "Unpin Post from Profile"
                        : "Pin Post to Profile"}
                    </span>
                  </DropdownDrawerItem>
                )}
                <DropdownDrawerItem
                  render={
                    <HidePostAction
                      {...{
                        post,
                        updatePost,
                        setLoading,
                      }}
                    />
                  }
                />
              </>
            )}
            <DropdownDrawerItem onSelect={onCopyLink}>
              <LuCopy className="mr-2 size-4 text-muted" />
              <span>Copy Link</span>
            </DropdownDrawerItem>

            <DropdownDrawerItem
              onSelect={() =>
                setDialog(DIALOG.PageShare, {
                  open: true,
                  replace: true,
                  params: {
                    pageLink: postLink,
                    referral: "false",
                    shareText: `Check this post I found on Focus by ${getUserDisplayName(post.poster as Account)}`,
                    title: "Share this post",
                  },
                })
              }
            >
              <LuShare className="mr-2 size-4 text-muted" />
              <span>Share</span>
            </DropdownDrawerItem>

            <DropdownDrawerItem
              render={
                <Link
                  href={`https://translate.google.com/?sl=auto&tl=zh-CN&text=${encodeURIComponent(post.body || "")}&op=translate`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center"
                >
                  <LuLanguages className="mr-2 size-4 text-muted" />
                  <span>Translate</span>
                </Link>
              }
            />
            {extraItems}
            {!isCurrentUserAccount && (
              <TriggerLoggedInModalWindow>
                <DropdownDrawerItem onClick={onBlockUser}>
                  <LuBan className="mr-2 size-4 text-muted" />
                  <span>Block User</span>
                </DropdownDrawerItem>
              </TriggerLoggedInModalWindow>
            )}
            <DropdownDrawerItem
              onSelect={() => openPostReport(DEFAULT_REPORT_TYPE, isElevated)}
            >
              <LuFlag className="mr-2 size-4 text-muted" />
              <span>Report Post</span>
            </DropdownDrawerItem>
            {isAdmin && <DropdownDrawerSeparator />}
            {isAdmin && (
              <DropdownDrawerItem onClick={onPinAdminPostClick}>
                <LuPin className="mr-2 size-4 text-muted" />
                <span>
                  {isPinnedByAdmin
                    ? "Unpin from Main Feed"
                    : "Pin to Main Feed"}
                </span>
              </DropdownDrawerItem>
            )}
            {isElevated && (
              <>
                <DropdownDrawerSeparator />
                <DropdownDrawerSub>
                  <DropdownDrawerSubTrigger>
                    <LuShield className="mr-2 size-4 text-muted" />
                    Moderation
                  </DropdownDrawerSubTrigger>
                  <DropdownDrawerSubContent title="Moderation">
                    <DropdownDrawerItem
                      onSelect={() =>
                        openPostReport(POST_REPORT_TYPE.HIDE_FROM_FOR_YOU.value)
                      }
                    >
                      <span>
                        {POST_REPORT_TYPE.HIDE_FROM_FOR_YOU.getLabel({
                          allowReverse: isAdmin,
                          hiddenFromForYou,
                        })}
                      </span>
                    </DropdownDrawerItem>
                    <DropdownDrawerItem
                      onSelect={() =>
                        openPostReport(POST_REPORT_TYPE.NSFW.value)
                      }
                    >
                      <span>
                        {POST_REPORT_TYPE.NSFW.getLabel({
                          allowReverse: isAdmin,
                          isNSFW: isPostNSFW,
                        })}
                      </span>
                    </DropdownDrawerItem>
                    <DropdownDrawerSeparator />
                    <DropdownDrawerItem
                      onSelect={() =>
                        openProfileReport(USER_REPORT_TYPE.NSFW.value)
                      }
                    >
                      <span>
                        {USER_REPORT_TYPE.NSFW.getLabel({
                          allowReverse: isAdmin,
                          isNSFW: isUserNSFW,
                        })}
                      </span>
                    </DropdownDrawerItem>
                    <DropdownDrawerItem
                      onSelect={() =>
                        openProfileReport(USER_REPORT_TYPE.GREYLIST.value)
                      }
                    >
                      <span>
                        {USER_REPORT_TYPE.GREYLIST.getLabel({
                          allowReverse: isAdmin,
                          isGreylisted: isUserGreylisted,
                        })}
                      </span>
                    </DropdownDrawerItem>
                    <DropdownDrawerItem
                      onSelect={() =>
                        openProfileReport(USER_REPORT_TYPE.BLACKLIST.value)
                      }
                    >
                      <span>
                        {USER_REPORT_TYPE.BLACKLIST.getLabel({
                          allowReverse: isAdmin,
                          isBlacklisted: isUserBlacklisted!,
                        })}
                      </span>
                    </DropdownDrawerItem>
                    {isSuperAdmin && (
                      <>
                        <DropdownDrawerSeparator />
                        <DropdownDrawerItem
                          onSelect={() => setPostWeightOpen(true)}
                        >
                          <span>Set Post Weight</span>
                        </DropdownDrawerItem>
                        <DropdownDrawerItem
                          onSelect={() => setUserWeightOpen(true)}
                        >
                          <span>Set User Weight</span>
                        </DropdownDrawerItem>
                      </>
                    )}
                  </DropdownDrawerSubContent>
                </DropdownDrawerSub>
              </>
            )}
          </DropdownDrawerContent>
        </DropdownDrawer>
      </div>
    );
  },
);

PostActionsDropdown.displayName = "PostActionsDropdown";
